import React, { createContext, useContext, ReactNode } from 'react';
import { PaymentMethodId, PaymentLocation } from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { TripType } from '../BookingWizard/FerryTripBookingWizard/BookingWizardTypes';

/**
 * Callback invoked after a payment completes successfully. The boolean indicates whether
 * the post-payment flow should also mark the booking as checked-in.
 */
type OnConfirmCallback = (confirmAndCheckIn: boolean) => Promise<void> | void;

/**
 * Optional pre-payment hook (e.g. check-in confirmation modal). Returns a boolean that
 * threads through to onConfirm. If it throws, the payment is aborted.
 */
type BeforePaymentCallback = () => Promise<boolean>;

/**
 * Immutable configuration for a single payment flow. Set via props on
 * <PaymentFlowProvider> and read by descendants via usePaymentFlow().
 *
 * This context is purely a data distribution layer — it holds no mutable state
 * and exposes no actions. It is recreated when the parent component remounts
 * (e.g. navigating between check-in and booking wizard), which naturally
 * prevents stale config from leaking between flows.
 */
export interface PaymentFlowConfig {
	transactionId: string;
	userId?: string;
	isAlteration: boolean;
	tripType?: TripType;
	eventBooking: boolean;
	paymentLocation: PaymentLocation;
	inCheckIn: boolean;
	includeSubmitButtons: boolean;
	onCancelRedirectUrl?: string;
	redirectUrl?: string;
	canInvoiceOverride?: boolean;
	onConfirm?: OnConfirmCallback;
	beforePayment?: BeforePaymentCallback;
}

const PaymentFlowContext = createContext<PaymentFlowConfig | null>(null);

export interface PaymentFlowProviderProps extends PaymentFlowConfig {
	children: ReactNode;
}

/**
 * Provides immutable payment flow configuration to descendants. All fields are
 * set via props and never mutated — if the flow changes, the parent remounts
 * the provider with new props.
 */
export function PaymentFlowProvider({ children, ...config }: PaymentFlowProviderProps) {
	return <PaymentFlowContext.Provider value={config}>{children}</PaymentFlowContext.Provider>;
}

/**
 * Read the current payment flow configuration. Throws if called outside a
 * <PaymentFlowProvider>.
 */
export function usePaymentFlow(): PaymentFlowConfig {
	const context = useContext(PaymentFlowContext);
	if (!context) {
		throw new Error('usePaymentFlow must be used within a PaymentFlowProvider');
	}
	return context;
}

export type { OnConfirmCallback, BeforePaymentCallback };
